import { ChangeEvent, FC, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { Button } from 'components/ui/Button';
import { Field, Label } from 'components/forms/fragments/FieldSet';
import { Input } from 'components/forms/fragments/Input';
import { GenericLoading, Sizes } from 'components/ui/Loader';
import { AlertCircle, FileText, RefreshCw } from 'lucide-react';
import { IdentityVerificationState } from '../useIdentityVerification';
import { DocumentPreview } from './DocumentPreview';

interface ManualFormProps {
  state: IdentityVerificationState;
  onReset: () => void;
  onRetryOcr: () => void;
  onSubmit: (firstName: string, lastName: string) => Promise<void>;
}

export const ManualForm: FC<ManualFormProps> = ({
  state,
  onReset,
  onRetryOcr,
  onSubmit
}) => {
  const { t } = useTranslation(NsKey.IdentityUpload);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  const canSubmit =
    !state.isSubmitting && firstName.trim().length > 0 && lastName.trim().length > 0;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(firstName.trim(), lastName.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <DocumentPreview state={state} compact />

      <div className="flex items-start gap-2 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-3 text-sm text-amber-800">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{t('IDENTITY_UPLOAD.DIALOG.MANUAL_FALLBACK_HINT')}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field>
          <Label htmlFor="identity-first-name" required>
            {t('IDENTITY_UPLOAD.DIALOG.FIRST_NAME')}
          </Label>
          <Input
            ref={firstInputRef}
            id="identity-first-name"
            name="firstName"
            autoComplete="given-name"
            value={firstName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
            disabled={state.isSubmitting}
          />
        </Field>
        <Field>
          <Label htmlFor="identity-last-name" required>
            {t('IDENTITY_UPLOAD.DIALOG.LAST_NAME')}
          </Label>
          <Input
            id="identity-last-name"
            name="lastName"
            autoComplete="family-name"
            value={lastName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
            disabled={state.isSubmitting}
          />
        </Field>
      </div>

      {state.mismatched && (
        <div
          role="alert"
          aria-live="polite"
          className="flex items-start gap-2 bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 text-sm text-red-700"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {state.errorMessage || t('IDENTITY_UPLOAD.ERRORS.MANUAL_MISMATCH')}
          </span>
        </div>
      )}

      {!state.mismatched && state.errorMessage && (
        <div
          role="alert"
          aria-live="polite"
          className="text-sm text-red-600 bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3"
        >
          {state.errorMessage}
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <Button outline onClick={onReset} disabled={state.isSubmitting}>
            {t('IDENTITY_UPLOAD.DIALOG.IMPORT_ANOTHER')}
          </Button>
          <Button outline onClick={onRetryOcr} disabled={state.isSubmitting}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('IDENTITY_UPLOAD.DIALOG.RETRY_OCR')}
          </Button>
        </div>
        <Button color="primary" type="submit" disabled={!canSubmit}>
          {state.isSubmitting ? (
            <>
              <GenericLoading size={Sizes.sm} isInline loading />
              <span className="ml-2">{t('IDENTITY_UPLOAD.DIALOG.SUBMITTING')}</span>
            </>
          ) : (
            <>
              <FileText className="w-4 h-4 mr-2" />
              {t('IDENTITY_UPLOAD.DIALOG.SUBMIT')}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
